"use client";

import React, { useEffect, useState } from "react";
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from "@/components/ui/carousel";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const HERO_IMAGES = [{
  desktopSrc: "https://saraandmichael2026.com/wp-content/uploads/2026/04/IMG_1270-scaled.jpeg",
  mobileSrc: "https://saraandmichael2026.com/wp-content/uploads/2026/04/IMG_1270-2-scaled.jpeg",
  alt: "Cinematic Roman sunset with a slow pan across the horizon",
  mobilePosition: "object-center",
  desktopPosition: "object-center"
}, {
  desktopSrc: "https://saraandmichael2026.com/wp-content/uploads/2026/04/IMG_1364-scaled.jpeg",
  mobileSrc: "https://saraandmichael2026.com/wp-content/uploads/2026/04/IMG_1364-1-scaled.jpeg",
  alt: "The Colosseum at dusk under a warm Italian sky",
  mobilePosition: "object-center",
  desktopPosition: "object-center"
}, {
  desktopSrc: "https://saraandmichael2026.com/wp-content/uploads/2026/04/a75bfb09-1e54-4f5f-872d-ceb6539e64c1-1.jpg",
  mobileSrc: "https://saraandmichael2026.com/wp-content/uploads/2026/04/a75bfb09-1e54-4f5f-872d-ceb6539e64c1-2.jpg",
  alt: "The elegant architecture of Villa Aurelia in Rome",
  mobilePosition: "object-center",
  desktopPosition: "object-center"
}];
type HeroLanguage = "en" | "it";
export default function WhereMemoriesBegin() {
  const [api, setApi] = useState<CarouselApi>();
  const [language, setLanguage] = useState<HeroLanguage>("en");
  const [countdown, setCountdown] = useState("");
  const {
    ref: textRef,
    isVisible: textVisible
  } = useRevealOnScroll();
  useEffect(() => {
    const savedLanguage = localStorage.getItem("site-language");
    if (savedLanguage === "en" || savedLanguage === "it") {
      setLanguage(savedLanguage);
    }
  }, []);
  useEffect(() => {
    const handleStorageChange = () => {
      const savedLanguage = localStorage.getItem("site-language");
      if (savedLanguage === "en" || savedLanguage === "it") {
        setLanguage(savedLanguage);
      }
    };
    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("focus", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("focus", handleStorageChange);
    };
  }, []);
  useEffect(() => {
    if (!api) return;
    const interval = setInterval(() => {
      api.scrollNext();
    }, 6000);
    return () => clearInterval(interval);
  }, [api]);

  // COUNTDOWN LOGIC (Italy time)
  useEffect(() => {
    const target = new Date("2026-09-11T16:00:00+02:00").getTime();
    const updateCountdown = () => {
      const now = new Date().getTime();
      const diff = target - now;
      if (diff <= 0) {
        setCountdown("0 d 0 h 0 min 0 s");
        return;
      }
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor(diff / (1000 * 60 * 60) % 24);
      const minutes = Math.floor(diff / (1000 * 60) % 60);
      const seconds = Math.floor(diff / 1000 % 60);
      setCountdown(`${days} d ${hours} h ${minutes} min ${seconds} s`);
    };
    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, []);
  const t = {
    en: {
      title: "Benvenuti a Roma",
      dateLocation: "September 11, 2026 · Rome",
      rsvp: "RSVP"
    },
    it: {
      title: "Benvenuti a Roma",
      dateLocation: "11 Settembre 2026 · Roma",
      rsvp: "RSVP"
    }
  }[language];
  return <section data-section-id="25411" id="benvenuti-a-roma" className="relative w-full overflow-hidden bg-background">
      <div className="relative w-full h-[75svh] md:mx-auto md:max-w-[1700px] md:h-[90svh] md:max-h-[950px]">
        <Carousel setApi={setApi} opts={{
        loop: true,
        duration: 60
      }} className="h-full w-full [&>div]:h-full">
          <CarouselContent className="-ml-0 h-full">
            {HERO_IMAGES.map((image, index) => <CarouselItem key={index} className="pl-0 basis-full h-full">
                <div className="relative w-full h-full">
                  <img src={image.mobileSrc} alt={image.alt} className={`md:hidden absolute inset-0 w-full h-full object-cover ${image.mobilePosition}`} />

                  <img src={image.desktopSrc} alt={image.alt} className={`hidden md:block absolute inset-0 w-full h-full object-cover ${image.desktopPosition}`} />

                  <div className="absolute inset-0 bg-black/30 md:bg-black/20" />
                </div>
              </CarouselItem>)}
          </CarouselContent>
        </Carousel>

        <div className="absolute inset-0 z-10 flex items-center justify-center px-5 text-center">
          <div ref={textRef} className={`mx-auto w-full max-w-4xl transition-all duration-1000 ease-out ${textVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}>
            <h1 className="font-serif italic text-[40px] leading-[1.05] tracking-tight drop-shadow-2xl sm:text-6xl md:text-[120px]" style={{
            color: "#e8d5a3",
            textShadow: "0 2px 20px rgba(0,0,0,0.6), 0 0 60px rgba(0,0,0,0.3)",
            WebkitTextStroke: "0.5px rgba(255,220,150,0.4)"
          }}>
              {t.title}
            </h1>

            <p className="mx-auto mt-4 max-w-[260px] font-sans text-[10px] uppercase tracking-[0.22em] text-white/90 drop-shadow-md sm:mt-6 sm:max-w-2xl sm:text-sm md:text-lg">
              {t.dateLocation}
            </p>

            {/* BUTTON + COUNTDOWN */}
            <div className="mt-7 sm:mt-10 flex flex-col items-center gap-4">
              <Button asChild size="lg" className="h-11 rounded-none bg-primary px-6 text-sm font-medium text-primary-foreground transition-all duration-300 hover:bg-primary/90 sm:h-14 sm:px-8 sm:text-lg">
                <Link to="/RSVPPage">{t.rsvp}</Link>
              </Button>

             <p className="mx-auto max-w-[260px] font-sans text-[10px] uppercase tracking-[0.22em] text-white/90 drop-shadow-md sm:max-w-2xl sm:text-sm md:text-lg">
  {countdown}
</p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}